/**
 * Control-plane API client.
 * - credentials: 'include' always (BFF httpOnly session cookie)
 * - CSRF: read chat_csrf cookie → x-csrf-token on POST/DELETE/PUT/PATCH
 * - Never stores Cognito tokens in localStorage
 */

import {
  ApiError,
  DEFAULT_FEATURE_FLAGS,
  type AdminFlag,
  type AdminInvite,
  type AdminInviteCreated,
  type AdminUser,
  type ApiErrorBody,
  type AuditEvent,
  type FeatureFlags,
  type FlagStatus,
  type InvitePreview,
  type LoginResponse,
  type McpClient,
  type MintedPat,
  type PatMeta,
  type PatScope,
  type UserProfile,
  type UserRole,
} from "./types.js";
import type {
  DevicesListResponse,
  PairingCode,
  UpdateAccountSettingsResponse,
} from "./chat.js";
import {
  apiBaseUrl,
  CSRF_COOKIE_NAME,
  CSRF_HEADER_NAME,
} from "./config.js";

const MUTATING = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** Read a cookie value by name (document.cookie). */
export function getCookie(name: string, cookieSource?: string): string | null {
  const raw =
    cookieSource ??
    (typeof document !== "undefined" ? document.cookie : "");
  if (!raw) return null;
  const parts = raw.split(";");
  for (const part of parts) {
    const trimmed = part.trim();
    const eq = trimmed.indexOf("=");
    if (eq < 0) continue;
    const key = trimmed.slice(0, eq).trim();
    if (key === name) {
      return decodeURIComponent(trimmed.slice(eq + 1).trim());
    }
  }
  return null;
}

export function getCsrfToken(cookieSource?: string): string | null {
  return getCookie(CSRF_COOKIE_NAME, cookieSource);
}

export function buildUrl(path: string): string {
  const base = apiBaseUrl();
  const p = path.startsWith("/") ? path : `/${path}`;
  return base ? `${base}${p}` : p;
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204) return null;
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { error: "invalid_json", error_description: text.slice(0, 200) };
  }
}

function errorFromBody(status: number, body: unknown): ApiError {
  const b = (body ?? {}) as ApiErrorBody;
  const code = typeof b.error === "string" ? b.error : `http_${status}`;
  const message =
    typeof b.error_description === "string"
      ? b.error_description
      : typeof b.error === "string"
        ? b.error
        : `Request failed (${status})`;
  return new ApiError(status, code, message, body);
}

export type RequestOptions = {
  method?: string;
  body?: unknown;
  /** Skip CSRF header (only for login, which uses Origin check). */
  skipCsrf?: boolean;
};

export async function apiRequest<T = unknown>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const method = (opts.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = {
    accept: "application/json",
  };

  if (opts.body !== undefined) {
    headers["content-type"] = "application/json";
  }

  if (MUTATING.has(method) && !opts.skipCsrf) {
    const csrf = getCsrfToken();
    if (csrf) {
      headers[CSRF_HEADER_NAME] = csrf;
    }
  }

  const res = await fetch(buildUrl(path), {
    method,
    credentials: "include",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  const body = await parseBody(res);
  if (!res.ok) {
    throw errorFromBody(res.status, body);
  }
  return body as T;
}

// ── Auth ──────────────────────────────────────────────────────────────────

export async function login(
  email: string,
  password: string,
): Promise<LoginResponse> {
  // Login CSRF uses Origin/Referer on the server — no prior csrf cookie.
  // password may be empty/ignored when the server's LOCAL_DEV_AUTH_BYPASS
  // flag is on; the field is always sent so this call works either way.
  return apiRequest<LoginResponse>("/api/v1/auth/login", {
    method: "POST",
    skipCsrf: true,
    body: { email, password },
  });
}

/** Public self-signup (gated by auth.selfSignup). Never logs password. */
export async function signUp(
  email: string,
  password: string,
): Promise<{
  ok: boolean;
  status?: "confirm_required" | "confirmed";
  message?: string;
  email?: string;
}> {
  return apiRequest("/api/v1/auth/signup", {
    method: "POST",
    skipCsrf: true,
    body: { email, password },
  });
}

/** Confirm self-signup with email verification code. */
export async function confirmSignUp(input: {
  email: string;
  code: string;
}): Promise<{ ok: boolean; message?: string; email?: string }> {
  return apiRequest("/api/v1/auth/confirm-signup", {
    method: "POST",
    skipCsrf: true,
    body: { email: input.email, code: input.code },
  });
}

/** Resend signup confirmation code (no existence leak). */
export async function resendConfirmation(
  email: string,
): Promise<{ ok: boolean; message?: string }> {
  return apiRequest("/api/v1/auth/resend-confirmation", {
    method: "POST",
    skipCsrf: true,
    body: { email },
  });
}

/** Start forgot-password (Cognito emails a code). Never reveals if email exists. */
export async function forgotPassword(
  email: string,
): Promise<{ ok: boolean; message?: string }> {
  return apiRequest("/api/v1/auth/forgot-password", {
    method: "POST",
    skipCsrf: true,
    body: { email },
  });
}

/** Complete forgot-password with email code + new password. */
export async function confirmForgotPassword(input: {
  email: string;
  code: string;
  newPassword: string;
}): Promise<{ ok: boolean; message?: string }> {
  return apiRequest("/api/v1/auth/confirm-forgot-password", {
    method: "POST",
    skipCsrf: true,
    body: {
      email: input.email,
      code: input.code,
      newPassword: input.newPassword,
    },
  });
}

export async function respondNewPassword(input: {
  session: string;
  email: string;
  newPassword: string;
}): Promise<LoginResponse> {
  return apiRequest<LoginResponse>("/api/v1/auth/login", {
    method: "POST",
    skipCsrf: true,
    body: {
      challengeName: "NEW_PASSWORD_REQUIRED",
      session: input.session,
      email: input.email,
      newPassword: input.newPassword,
    },
  });
}

export async function respondTotp(input: {
  session: string;
  email: string;
  totpCode: string;
}): Promise<LoginResponse> {
  return apiRequest<LoginResponse>("/api/v1/auth/login", {
    method: "POST",
    skipCsrf: true,
    body: {
      challengeName: "SOFTWARE_TOKEN_MFA",
      session: input.session,
      email: input.email,
      totpCode: input.totpCode,
    },
  });
}

export async function logout(): Promise<void> {
  await apiRequest<null>("/api/v1/auth/logout", { method: "POST" });
}

export async function getMe(): Promise<UserProfile> {
  return apiRequest<UserProfile>("/api/v1/auth/me");
}

/** PATCH /api/v1/auth/me — update display name. */
export async function updateProfile(input: {
  displayName: string;
}): Promise<UserProfile> {
  return apiRequest<UserProfile>("/api/v1/auth/me", {
    method: "PATCH",
    body: input,
  });
}

/** POST /api/v1/auth/change-password — signed-in password change. */
export async function changePassword(input: {
  previousPassword: string;
  newPassword: string;
}): Promise<{ ok: boolean; message?: string }> {
  return apiRequest("/api/v1/auth/change-password", {
    method: "POST",
    body: input,
  });
}

// ── Mac adapter download ───────────────────────────────────────────────────

/** Mac adapter installer metadata from GET /api/v1/adapter (public). */
export type AdapterDownloadInfo = {
  available: boolean;
  version: string;
  filename: string;
  downloadUrl: string;
  notes: string;
};

/**
 * GET /api/v1/adapter — current Mac adapter DMG URL when hosted.
 * Soft-fails to available:false so the Devices page still loads offline.
 */
export async function getAdapterDownload(): Promise<AdapterDownloadInfo> {
  try {
    const res = await apiRequest<{ adapter?: AdapterDownloadInfo }>(
      "/api/v1/adapter",
    );
    const a = res.adapter;
    if (!a || typeof a !== "object") {
      return {
        available: false,
        version: "",
        filename: "",
        downloadUrl: "",
        notes: "",
      };
    }
    return {
      available: a.available === true && Boolean(a.downloadUrl),
      version: typeof a.version === "string" ? a.version : "",
      filename: typeof a.filename === "string" ? a.filename : "",
      downloadUrl: typeof a.downloadUrl === "string" ? a.downloadUrl : "",
      notes: typeof a.notes === "string" ? a.notes : "",
    };
  } catch {
    return {
      available: false,
      version: "",
      filename: "",
      downloadUrl: "",
      notes: "",
    };
  }
}

// ── Devices + iMessage accounts (Phase 4a) ─────────────────────────────────

/** GET /api/v1/devices */
export async function listDevices(): Promise<DevicesListResponse> {
  const res = await apiRequest<Partial<DevicesListResponse>>(
    "/api/v1/devices",
  );
  return { devices: res.devices ?? [], accounts: res.accounts ?? [] };
}

/** POST /api/v1/devices/pair — mints an 8-char pairing code, 5-minute TTL. */
export async function mintPairingCode(): Promise<PairingCode> {
  return apiRequest<PairingCode>("/api/v1/devices/pair", { method: "POST" });
}

/** DELETE /api/v1/devices/:deviceId — revoke a paired Mac immediately. */
export async function revokeDevice(deviceId: string): Promise<void> {
  await apiRequest<null>(
    `/api/v1/devices/${encodeURIComponent(deviceId)}`,
    { method: "DELETE" },
  );
}

/**
 * POST /api/v1/devices/:deviceId/open-messages
 * Ask the Mac adapter to launch Messages.app (minimized by default).
 */
export async function openMessagesOnDevice(
  deviceId: string,
  opts?: { minimized?: boolean },
): Promise<{
  deviceId: string;
  launched: boolean;
  messagesRunning: boolean;
  minimized: boolean;
}> {
  return apiRequest(
    `/api/v1/devices/${encodeURIComponent(deviceId)}/open-messages`,
    {
      method: "POST",
      body: { minimized: opts?.minimized ?? true },
    },
  );
}

/**
 * PATCH /api/v1/accounts/:accountId — update autonomous-sends / allowlist /
 * label (plan § A5). At least one field is required; partial updates are
 * supported.
 */
export async function updateAccountSettings(
  accountId: string,
  patch: {
    autonomousSends?: boolean;
    allowedChatIds?: string[];
    allowContactsSends?: boolean;
    label?: string;
  },
): Promise<UpdateAccountSettingsResponse> {
  return apiRequest<UpdateAccountSettingsResponse>(
    `/api/v1/accounts/${encodeURIComponent(accountId)}`,
    { method: "PATCH", body: patch },
  );
}

// ── Features ──────────────────────────────────────────────────────────────

/**
 * Session-authenticated public feature flags (B0).
 * Falls back to client defaults only when the caller opts in via tryGetFeatures.
 */
export async function getFeatures(): Promise<FeatureFlags> {
  const res = await apiRequest<{ features: FeatureFlags }>("/api/v1/features");
  return { ...DEFAULT_FEATURE_FLAGS, ...(res.features ?? {}) };
}

/**
 * Best-effort features fetch for SPA boot.
 * Missing route / network → defaults (scopesEnforce off).
 */
export async function tryGetFeatures(): Promise<FeatureFlags> {
  try {
    return await getFeatures();
  } catch {
    return { ...DEFAULT_FEATURE_FLAGS };
  }
}

// ── PATs ──────────────────────────────────────────────────────────────────

export async function listTokens(): Promise<PatMeta[]> {
  const res = await apiRequest<{ tokens: PatMeta[] }>("/api/v1/tokens");
  return res.tokens ?? [];
}

export async function mintToken(input: {
  name: string;
  expiresInDays?: number;
  /** When set, dual-written if server `pats.scopesWrite` is on (B0+). */
  scopes?: PatScope[];
}): Promise<MintedPat> {
  const body: Record<string, unknown> = {
    name: input.name,
  };
  if (input.expiresInDays !== undefined) {
    body["expiresInDays"] = input.expiresInDays;
  }
  if (input.scopes !== undefined) {
    body["scopes"] = input.scopes;
  }
  return apiRequest<MintedPat>("/api/v1/tokens", {
    method: "POST",
    body,
  });
}

export async function revokeToken(tokenId: string): Promise<void> {
  await apiRequest<null>(
    `/api/v1/tokens/${encodeURIComponent(tokenId)}`,
    { method: "DELETE" },
  );
}

// ── MCP clients (OAuth + PAT connections) ─────────────────────────────────

export async function listMcpClients(): Promise<McpClient[]> {
  const res = await apiRequest<{ clients: McpClient[] }>("/api/v1/mcp-clients");
  return res.clients ?? [];
}

export async function disconnectMcpClient(clientId: string): Promise<void> {
  await apiRequest<{ ok?: boolean }>(
    `/api/v1/mcp-clients/${encodeURIComponent(clientId)}`,
    { method: "DELETE" },
  );
}

// ── Invites (accept SPA) ────────────────────────────────────────────────

export type InviteAcceptResult = {
  id?: string;
  email: string;
  status: string;
  userId?: string;
  role?: string;
  inviteId?: string;
};

/**
 * Pre-login safe metadata for accept UI. Does not consume the invite.
 * Token goes in the query string (GET); never log it.
 */
export async function previewInvite(token: string): Promise<InvitePreview> {
  const q = new URLSearchParams({ token });
  return apiRequest<InvitePreview>(
    `/api/v1/invites/preview?${q.toString()}`,
  );
}

/**
 * Accept invite after BFF session login (CSRF required when cookie present).
 * Anonymous POST returns pending_login without consuming.
 */
export async function acceptInvite(
  token: string,
): Promise<InviteAcceptResult> {
  return apiRequest<InviteAcceptResult>("/api/v1/invites/accept", {
    method: "POST",
    body: { token },
  });
}

// ── Audit (own events) ────────────────────────────────────────────────────

export type AuditPage = {
  events: AuditEvent[];
  nextCursor?: string;
};

/** GET /api/v1/audit — session user's recent audit events (newest first). */
export async function listAudit(opts?: {
  limit?: number;
  cursor?: string;
}): Promise<AuditPage> {
  const params = new URLSearchParams();
  if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts?.cursor) params.set("cursor", opts.cursor);
  const qs = params.toString();
  const path = qs ? `/api/v1/audit?${qs}` : "/api/v1/audit";
  const res = await apiRequest<{
    events?: AuditEvent[];
    nextCursor?: string;
  }>(path);
  const page: AuditPage = { events: res.events ?? [] };
  if (typeof res.nextCursor === "string" && res.nextCursor) {
    page.nextCursor = res.nextCursor;
  }
  return page;
}

// ── Admin anomaly flags ───────────────────────────────────────────────────

export type AdminFlagsPage = {
  flags: AdminFlag[];
  nextCursor?: string;
};

/** GET /api/v1/admin/flags — owner/admin only. */
export async function listAdminFlags(opts?: {
  limit?: number;
  cursor?: string;
  status?: FlagStatus;
}): Promise<AdminFlagsPage> {
  const params = new URLSearchParams();
  if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts?.cursor) params.set("cursor", opts.cursor);
  if (opts?.status) params.set("status", opts.status);
  const qs = params.toString();
  const path = qs
    ? `/api/v1/admin/flags?${qs}`
    : "/api/v1/admin/flags";
  const res = await apiRequest<{
    flags?: AdminFlag[];
    nextCursor?: string;
  }>(path);
  const page: AdminFlagsPage = { flags: res.flags ?? [] };
  if (typeof res.nextCursor === "string" && res.nextCursor) {
    page.nextCursor = res.nextCursor;
  }
  return page;
}

/** POST /api/v1/admin/flags/:flagId/ack — owner/admin only. */
export async function ackAdminFlag(flagId: string): Promise<AdminFlag> {
  return apiRequest<AdminFlag>(
    `/api/v1/admin/flags/${encodeURIComponent(flagId)}/ack`,
    { method: "POST" },
  );
}

// ── Admin invites (productized signup) ────────────────────────────────────

/** GET /api/v1/admin/invites — owner/admin; requires invites.productized. */
export async function listAdminInvites(): Promise<AdminInvite[]> {
  const res = await apiRequest<{ invites?: AdminInvite[] }>(
    "/api/v1/admin/invites",
  );
  return res.invites ?? [];
}

/** POST /api/v1/admin/invites — returns one-time inviteUrl. */
export async function createAdminInvite(input: {
  email: string;
  role?: "user" | "admin";
}): Promise<AdminInviteCreated> {
  return apiRequest<AdminInviteCreated>("/api/v1/admin/invites", {
    method: "POST",
    body: input,
  });
}

/** POST /api/v1/admin/invites/:id/resend — rotates token; new inviteUrl once. */
export async function resendAdminInvite(
  inviteId: string,
): Promise<AdminInviteCreated> {
  return apiRequest<AdminInviteCreated>(
    `/api/v1/admin/invites/${encodeURIComponent(inviteId)}/resend`,
    { method: "POST" },
  );
}

/** DELETE /api/v1/admin/invites/:id — revoke pending invite. */
export async function revokeAdminInvite(
  inviteId: string,
): Promise<AdminInvite> {
  return apiRequest<AdminInvite>(
    `/api/v1/admin/invites/${encodeURIComponent(inviteId)}`,
    { method: "DELETE" },
  );
}

// ── Admin users ───────────────────────────────────────────────────────────

export type AdminUserListOpts = {
  /** Substring match on email or userId. */
  q?: string;
  role?: UserRole | "all";
  status?: "active" | "disabled" | "all";
};

/** GET /api/v1/admin/users?q=&role=&status= — owner/admin. */
export async function listAdminUsers(
  opts?: AdminUserListOpts,
): Promise<AdminUser[]> {
  const params = new URLSearchParams();
  if (opts?.q?.trim()) params.set("q", opts.q.trim());
  if (opts?.role && opts.role !== "all") params.set("role", opts.role);
  if (opts?.status && opts.status !== "all") params.set("status", opts.status);
  const qs = params.toString();
  const path = qs ? `/api/v1/admin/users?${qs}` : "/api/v1/admin/users";
  const res = await apiRequest<{ users?: AdminUser[] }>(path);
  return res.users ?? [];
}

/** GET /api/v1/admin/users/:id/audit — admin view of that user's activity. */
export async function listAdminUserAudit(
  userId: string,
  opts?: { limit?: number; cursor?: string },
): Promise<{
  userId: string;
  email: string;
  events: AuditEvent[];
  nextCursor?: string | null;
}> {
  const params = new URLSearchParams();
  if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts?.cursor) params.set("cursor", opts.cursor);
  const qs = params.toString();
  const base = `/api/v1/admin/users/${encodeURIComponent(userId)}/audit`;
  const path = qs ? `${base}?${qs}` : base;
  const res = await apiRequest<{
    userId: string;
    email: string;
    events?: AuditEvent[];
    nextCursor?: string;
  }>(path);
  return {
    userId: res.userId,
    email: res.email,
    events: res.events ?? [],
    nextCursor: res.nextCursor ?? null,
  };
}

/** POST /api/v1/admin/users/:id/disable */
export async function disableAdminUser(
  userId: string,
): Promise<AdminUser & { tokenVersion?: number }> {
  return apiRequest(
    `/api/v1/admin/users/${encodeURIComponent(userId)}/disable`,
    { method: "POST" },
  );
}

/** POST /api/v1/admin/users/:id/enable */
export async function enableAdminUser(
  userId: string,
): Promise<AdminUser & { tokenVersion?: number }> {
  return apiRequest(
    `/api/v1/admin/users/${encodeURIComponent(userId)}/enable`,
    { method: "POST" },
  );
}

/** POST /api/v1/admin/users/:id/revoke-pats */
export async function revokeAdminUserPats(
  userId: string,
): Promise<{ userId: string; tokenVersion: number }> {
  return apiRequest(
    `/api/v1/admin/users/${encodeURIComponent(userId)}/revoke-pats`,
    { method: "POST" },
  );
}

/** PATCH /api/v1/admin/users/:id — owner only; body { role }. */
export async function patchAdminUserRole(
  userId: string,
  role: UserRole,
): Promise<AdminUser> {
  return apiRequest<AdminUser>(
    `/api/v1/admin/users/${encodeURIComponent(userId)}`,
    { method: "PATCH", body: { role } },
  );
}

/** POST /api/v1/admin/users/:id/reset-password — Cognito recovery email. */
export async function resetAdminUserPassword(
  userId: string,
): Promise<{ ok: boolean; userId: string; email?: string; message?: string }> {
  return apiRequest(
    `/api/v1/admin/users/${encodeURIComponent(userId)}/reset-password`,
    { method: "POST" },
  );
}

/** DELETE /api/v1/admin/users/:id — identity + Cognito + allowlist. */
export async function deleteAdminUser(
  userId: string,
): Promise<{ ok: boolean; userId: string; email?: string }> {
  return apiRequest(
    `/api/v1/admin/users/${encodeURIComponent(userId)}`,
    { method: "DELETE" },
  );
}

/**
 * GET /api/v1/admin/users/export — download users CSV.
 * Uses fetch + blob so Content-Disposition attachment works.
 */
export async function exportAdminUsersCsv(): Promise<void> {
  const method = "GET";
  const headers: Record<string, string> = {
    accept: "text/csv, application/json",
  };
  const res = await fetch(buildUrl("/api/v1/admin/users/export"), {
    method,
    credentials: "include",
    headers,
  });
  if (!res.ok) {
    const body = await parseBody(res);
    throw errorFromBody(res.status, body);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = "users.csv";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
